import React, { useMemo, useRef, useCallback } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Table } from '../OrderDetails/components/Table'
import { Button } from '../../components/shadcn/Button'
import { Checkbox } from '../../components/Checkbox'
import { graphql } from '../../gql'
import { FragmentValue } from '../../lib/RequestUtils'

export const DisposalTable_DisposalFragment = graphql(`
  fragment DisposalCreateTable_PackageFragment on Package {
    id
    code
    order {
      id
      carrier {
        name
      }
      company {
        id
        name
      }
      shippingAddress {
        firstName
        lastName
        email
        phone
        street
        city
        zip
        country
      }
    }
  }
`)

type Props = {
  packages: FragmentValue<typeof DisposalTable_DisposalFragment>[]
  selectedIds: number[]
  onToggleOne: (id: number) => void
  onToggleAll: () => void
  onToggleRange?: (ids: number[]) => void
  isDisabled?: boolean
  showCarrier: boolean
  showCompany: boolean
}

const columnHelper = createColumnHelper<FragmentValue<typeof DisposalTable_DisposalFragment>>()

export const DisposalCreateTable: React.FC<Props> = ({
  packages,
  selectedIds,
  onToggleOne,
  onToggleAll,
  onToggleRange,
  isDisabled,
  showCarrier,
  showCompany,
}) => {
  const lastClickedIndexRef = useRef<number | null>(null)

  const handleCheckboxClick = useCallback(
    (id: number, index: number, event: React.MouseEvent) => {
      if (event.shiftKey && lastClickedIndexRef.current !== null) {
        // Shift-click: select range
        const startIndex = Math.min(lastClickedIndexRef.current, index)
        const endIndex = Math.max(lastClickedIndexRef.current, index)
        const rangeIds = packages.slice(startIndex, endIndex + 1).map(pkg => pkg.id)

        if (onToggleRange) {
          onToggleRange(rangeIds)
        } else {
          // Fallback: toggle each individually
          rangeIds.forEach(rangeId => {
            if (!selectedIds.includes(rangeId)) {
              onToggleOne(rangeId)
            }
          })
        }
      } else {
        // Normal click
        onToggleOne(id)
        lastClickedIndexRef.current = index
      }
    },
    [packages, selectedIds, onToggleOne, onToggleRange],
  )

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => (
          <div className="flex justify-start">
            <Checkbox checked={selectedIds.length === packages.length} onChange={onToggleAll} />
          </div>
        ),
        cell: ({ row }) => (
          <div
            className="flex justify-start flex-1 cursor-pointer"
            onClick={(e: React.MouseEvent) => {
              if (!isDisabled) {
                e.stopPropagation()
                handleCheckboxClick(row.original.id, row.index, e)
              }
            }}>
            <Checkbox
              checked={selectedIds.includes(row.original.id)}
              onChange={(e: any) => e.stopPropagation()}
              disabled={!!isDisabled}
            />
          </div>
        ),
        size: 60,
      }),
      columnHelper.accessor('code', {
        header: 'Kód balíku',
        cell: ({ getValue }) => getValue() || '-',
        size: 150,
      }),
      columnHelper.accessor('order.shippingAddress', {
        id: 'recipient',
        header: 'Příjemce',
        cell: ({ getValue }) => {
          const sa = getValue()
          const firstName = sa?.firstName || ''
          const lastName = sa?.lastName || ''
          return `${firstName} ${lastName}`.trim() || '-'
        },
        size: 200,
      }),
      columnHelper.accessor('order.shippingAddress', {
        id: 'contact',
        header: 'Kontakt',
        cell: ({ getValue }) => {
          const sa = getValue()
          const email = sa?.email || ''
          const phone = sa?.phone || ''
          return `${email} ${phone}`.trim() || '-'
        },
      }),
      columnHelper.accessor('order.shippingAddress', {
        id: 'address',
        header: 'Adresa',
        cell: ({ getValue }) => {
          const sa = getValue()
          const street = sa?.street || ''
          const city = sa?.city || ''
          return `${street} ${city}`.trim() || '-'
        },
        size: 280,
      }),
      columnHelper.accessor('order.shippingAddress', {
        id: 'zipCountry',
        header: 'PSČ / stát',
        cell: ({ getValue }) => {
          const sa = getValue()
          const zip = sa?.zip || ''
          const country = sa?.country || ''
          return `${zip} ${country}`.trim() || '-'
        },
        size: 160,
      }),
      columnHelper.accessor('order.carrier.name', {
        id: 'carrier',
        header: 'Dopravce',
        cell: ({ getValue }) => getValue() || '-',
        size: 120,
      }),
      columnHelper.accessor('order.company.name', {
        id: 'company',
        header: 'Společnost',
        size: 120,
      }),
    ],
    [onToggleAll, handleCheckboxClick, selectedIds, isDisabled],
  )

  const columnVisibility = useMemo(
    () => ({
      carrier: showCarrier,
      company: showCompany,
    }),
    [showCarrier, showCompany],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const table = useReactTable({
    columns,
    data: packages,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
    defaultColumn: {
      // @ts-ignore
      size: '1fr',
    },
  })

  return (
    <div className="rounded-xl border">
      <Table table={table} className="pl-1" getClassnameForRow={() => `pl-1`} fixedWidth="1400px" />
    </div>
  )
}

export default DisposalCreateTable
